import { useEffect, useState } from "react";
import { CalendarDays, ChevronRight, Handshake, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import ReservaDetalhesModal, { type ReservaDetalheModal } from "@/components/ReservaDetalhesModal";

type Reserva = {
  id: number;
  tutor_nome?: string;
  servico_nome?: string;
  status?: string;
  data_inicio?: string;
  data_fim?: string;
  hora_inicio?: string;
  hora_fim?: string;
  pode_avaliar?: boolean;
  ja_avaliou?: boolean;
  avaliacao_url?: string;
  label_avaliacao?: string;
};

type CollabOption = {
  id: number;
  anfitriao_id: number;
  anfitriao_nome: string;
  cidade?: string | null;
  estado?: string | null;
  servico_nome: string;
  preco_base: number;
};

const statusColors: Record<string, string> = {
  confirmada: "bg-green-100 text-green-700",
  pendente: "bg-yellow-100 text-yellow-700",
  cancelada: "bg-red-100 text-red-700",
  recusada: "bg-red-100 text-red-700",
  encaminhada: "bg-purple-100 text-purple-700",
  concluida: "bg-blue-100 text-blue-700",
};

function formatStatus(status?: string) {
  if (!status) return "Sem status";
  return status.charAt(0).toUpperCase() + status.slice(1).replaceAll("_", " ");
}

function csrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
}

async function fetchJson(url: string, options: RequestInit = {}) {
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...(options.headers || {}),
    },
    ...options,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok || data?.ok === false) {
    throw new Error(data?.message || "Não foi possível concluir a operação.");
  }

  return data;
}

async function postJson(url: string) {
  return fetchJson(url, {
    method: "POST",
    headers: {
      "X-CSRF-TOKEN": csrfToken(),
    },
  });
}

export default function AnfitriaoReservas() {
  const [reservas, setReservas] = useState<Reserva[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReserva, setSelectedReserva] = useState<Reserva | null>(null);
  const [collabOptions, setCollabOptions] = useState<CollabOption[]>([]);
  const [selectedServicoId, setSelectedServicoId] = useState<string>("");
  const [reason, setReason] = useState("Sem disponibilidade");
  const [message, setMessage] = useState("Olá! Recebi esta solicitação, mas não consigo atender. Você consegue avaliar essa reserva?");
  const [modalLoading, setModalLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [modalReservaOpen, setModalReservaOpen] = useState(false);
  const [reservaSelecionada, setReservaSelecionada] = useState<ReservaDetalheModal | null>(null);
  const [acaoReservaCarregando, setAcaoReservaCarregando] = useState<"aceitar" | "recusar" | "checkin" | "checkout" | null>(null);

  const loadReservas = () => {
    setLoading(true);

    fetchJson("/anfitriao/dashboard/data")
      .then((data) => {
        setReservas(data?.reservas || data?.proximas_reservas || []);
      })
      .catch((err) => {
        console.error("Erro ao carregar reservas:", err);
        setError("Não foi possível carregar as reservas.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadReservas();
  }, []);

  const openCollabModal = async (reserva: Reserva) => {
    setSelectedReserva(reserva);
    setCollabOptions([]);
    setSelectedServicoId("");
    setFeedback(null);
    setError(null);
    setModalLoading(true);

    try {
      const data = await fetchJson(`/anfitriao/reservas/${reserva.id}/colabs/options`);
      const options = data?.options || [];
      setCollabOptions(options);
      if (options.length > 0) {
        setSelectedServicoId(String(options[0].id));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar colegas para colab.");
    } finally {
      setModalLoading(false);
    }
  };

  const closeCollabModal = () => {
    if (submitting) return;
    setSelectedReserva(null);
    setCollabOptions([]);
    setSelectedServicoId("");
    setFeedback(null);
    setError(null);
  };

  const submitCollab = async () => {
    if (!selectedReserva || !selectedServicoId) return;

    setSubmitting(true);
    setFeedback(null);
    setError(null);

    try {
      await fetchJson(`/anfitriao/reservas/${selectedReserva.id}/colabs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-TOKEN": csrfToken(),
        },
        body: JSON.stringify({
          to_servico_id: Number(selectedServicoId),
          reason,
          message,
        }),
      });

      setFeedback("Reserva encaminhada para o colega. A colab está pendente de aceite.");
      atualizarReservaLocal(String(selectedReserva.id), { status: "encaminhada" });
      loadReservas();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível criar a colab.");
    } finally {
      setSubmitting(false);
    }
  };

  async function abrirDetalhesReserva(reserva: Reserva) {
    try {
      const dados = await fetchJson(`/reserva/${reserva.id}/detalhes-json`);

      setReservaSelecionada({
        id: dados.id,
        tutor: dados.tutor || dados.tutor_nome || reserva.tutor_nome || "Tutor",
        tutorId: dados.tutor_id || "",
        telefoneTutor: dados.telefone_tutor || "",
        servico: dados.servico || reserva.servico_nome || "Serviço",
        modalidadeServico: dados.modalidade_servico || "",
        status: dados.status || reserva.status || "pendente",
        dataInicio: dados.data_inicio || reserva.data_inicio || "",
        dataFim: dados.data_fim || reserva.data_fim || "",
        horaInicio: dados.hora_inicio || reserva.hora_inicio || "",
        horaFim: dados.hora_fim || reserva.hora_fim || "",
        checkinRealizadoEm: dados.checkin_realizado_em || null,
        checkoutRealizadoEm: dados.checkout_realizado_em || null,
        mensagem: dados.mensagem || "",
        valorLiquido: dados.valor_liquido ?? dados.pagamento?.valor_liquido ?? null,
        adicionalCheckoutValor: dados.adicional_checkout_valor ?? null,
        adicionalCheckoutTipo: dados.adicional_checkout_tipo ?? null,
        adicionalCheckoutStatus: dados.adicional_checkout_status ?? null,
        checkoutAdicionalUrl: dados.checkout_adicional_url ?? null,
        checkoutAdicionalExternoUrl: dados.checkout_adicional_externo_url ?? null,
        checkoutAdicionalWhatsappUrl: dados.checkout_adicional_whatsapp_url ?? null,
        avaliacao: dados.avaliacao ?? null,
        pets: Array.isArray(dados.pets) ? dados.pets : [],
        chatUrl: dados.chat_url || "/chat",
      });

      setModalReservaOpen(true);
    } catch (err) {
      console.error(err);
      alert("Não foi possível carregar os detalhes da reserva.");
    }
  }

  function fecharDetalhesReserva() {
    setModalReservaOpen(false);
    setReservaSelecionada(null);
  }

  function atualizarReservaLocal(id: string, updates: Partial<ReservaDetalheModal>) {
    setReservaSelecionada((atual) => (atual && String(atual.id) === id ? { ...atual, ...updates } : atual));

    setReservas((atuais) =>
      atuais.map((item) =>
        String(item.id) === id
          ? {
              ...item,
              status: typeof updates.status === "string" && updates.status ? updates.status : item.status,
            }
          : item
      )
    );
  }

  async function executarAcaoReserva(
    id: string,
    acao: "aceitar" | "recusar" | "checkin" | "checkout",
    endpoint: string,
    updates: Partial<ReservaDetalheModal>
  ) {
    if (acaoReservaCarregando) return;

    try {
      setAcaoReservaCarregando(acao);
      await postJson(endpoint);
      atualizarReservaLocal(id, updates);
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : "Não foi possível concluir a ação.");
    } finally {
      setAcaoReservaCarregando(null);
    }
  }

  async function aceitarReserva(id: string) {
    await executarAcaoReserva(id, "aceitar", `/anfitriao/reservas/${id}/aceitar`, { status: "confirmada" });
  }

  async function recusarReserva(id: string) {
    if (!window.confirm("Tem certeza que deseja recusar esta reserva?")) return;
    await executarAcaoReserva(id, "recusar", `/anfitriao/reservas/${id}/recusar`, { status: "recusada" });
  }

  async function realizarCheckin(id: string) {
    await executarAcaoReserva(id, "checkin", `/anfitriao/reservas/${id}/check-in`, {
      checkinRealizadoEm: new Date().toISOString(),
    });
  }

  async function realizarCheckout(id: string) {
    await executarAcaoReserva(id, "checkout", `/anfitriao/reservas/${id}/check-out`, {
      checkoutRealizadoEm: new Date().toISOString(),
      status: "concluida",
    });
  }

  return (
    <div className="p-4 md:p-6">
      <Card className="card-shadow border-0">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-forest" />
            Todas as Reservas
          </CardTitle>

          <Button asChild variant="link" size="sm" className="text-xs font-semibold">
            <a href="/anfitriao/dashboard">
              Voltar <ChevronRight className="h-3.5 w-3.5" />
            </a>
          </Button>
        </CardHeader>

        <CardContent>
          {error && !selectedReserva ? (
            <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
          ) : null}

          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Carregando reservas...</div>
          ) : reservas.length > 0 ? (
            <div className="space-y-3">
              {reservas.map((r) => (
                <div key={r.id} className="flex items-start gap-3 rounded-xl bg-muted/50 p-3 transition-colors hover:bg-muted">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-forest-light text-forest text-sm font-bold">
                      {(r.tutor_nome || "T").charAt(0)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-foreground">{r.tutor_nome || "Tutor"}</span>

                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${statusColors[r.status || ""] || "bg-muted text-muted-foreground"}`}>
                        {formatStatus(r.status)}
                      </span>
                    </div>

                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {r.servico_nome || "Serviço"} • {r.data_inicio}
                      {r.hora_inicio ? ` • ${r.hora_inicio}` : ""}
                      {r.data_fim ? ` → ${r.data_fim}` : ""}
                      {r.hora_fim ? ` • ${r.hora_fim}` : ""}
                    </p>

                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button type="button" variant="outline" size="sm" className="text-xs h-8" onClick={() => abrirDetalhesReserva(r)}>
                        Ver detalhes
                      </Button>

                      <Button asChild variant="ghost" size="sm" className="text-xs h-8">
                        <a href="/chat">Abrir chat</a>
                      </Button>

                      {r.status === "pendente" ? (
                        <Button type="button" variant="outline" size="sm" className="text-xs h-8" onClick={() => openCollabModal(r)}>
                          <Handshake className="mr-1 h-3.5 w-3.5" />
                          Encaminhar para colega
                        </Button>
                      ) : null}

                      {r.pode_avaliar && r.avaliacao_url ? (
                        <Button asChild size="sm" className="text-xs h-8">
                          <a href={r.avaliacao_url}>{r.label_avaliacao || "Avaliar"}</a>
                        </Button>
                      ) : r.ja_avaliou ? (
                        <Button variant="secondary" size="sm" className="text-xs h-8" disabled>
                          Avaliação enviada
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <span className="text-4xl">📅</span>
              <p className="mt-2 text-sm text-muted-foreground">Nenhuma reserva encontrada.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedReserva ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-background p-5 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold">Encaminhar reserva para colega</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Reserva #{selectedReserva.id} • {selectedReserva.servico_nome || "Serviço"} • {selectedReserva.tutor_nome || "Tutor"}
                </p>
              </div>

              <button type="button" className="rounded-full p-1 text-muted-foreground hover:bg-muted" onClick={closeCollabModal} aria-label="Fechar">
                <X className="h-5 w-5" />
              </button>
            </div>

            {error ? <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
            {feedback ? <div className="mb-3 rounded-lg bg-green-50 p-3 text-sm text-green-700">{feedback}</div> : null}

            {modalLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">Carregando colegas disponíveis...</div>
            ) : collabOptions.length > 0 ? (
              <div className="space-y-4">
                <label className="block text-sm font-medium">
                  Colega / serviço destino
                  <select className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm" value={selectedServicoId} onChange={(event) => setSelectedServicoId(event.target.value)}>
                    {collabOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.anfitriao_nome} — {option.servico_nome}
                        {option.cidade ? ` — ${option.cidade}${option.estado ? `/${option.estado}` : ""}` : ""}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block text-sm font-medium">
                  Motivo
                  <select className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm" value={reason} onChange={(event) => setReason(event.target.value)}>
                    <option>Sem disponibilidade</option>
                    <option>Fora da região</option>
                    <option>Serviço incompatível</option>
                    <option>Pet exige cuidado específico</option>
                    <option>Outro</option>
                  </select>
                </label>

                <label className="block text-sm font-medium">
                  Mensagem opcional
                  <textarea className="mt-1 min-h-24 w-full rounded-lg border bg-background px-3 py-2 text-sm" value={message} onChange={(event) => setMessage(event.target.value)} />
                </label>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="ghost" onClick={closeCollabModal} disabled={submitting}>Fechar</Button>
                  <Button type="button" onClick={submitCollab} disabled={submitting || !!feedback}>{submitting ? "Enviando..." : "Enviar colab"}</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">Nenhum colega ativo com o mesmo tipo de serviço foi encontrado para esta reserva.</div>
                <div className="flex justify-end">
                  <Button type="button" variant="ghost" onClick={closeCollabModal}>Fechar</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      <ReservaDetalhesModal
        open={modalReservaOpen}
        onClose={fecharDetalhesReserva}
        reserva={reservaSelecionada}
        viewerRole="anfitriao"
        onAceitar={aceitarReserva}
        onRecusar={recusarReserva}
        onRealizarCheckin={realizarCheckin}
        onRealizarCheckout={realizarCheckout}
        acaoReservaCarregando={acaoReservaCarregando}
      />
    </div>
  );
}
